import { AUTHENTICATION_V2_ENDPOINT } from '@dtx-company/true-common/src/constants/endpoints'
import {
  ClientSideError,
  ClientSideErrorType,
  FlowError,
  cloneErrorObject,
  logger
} from '@dtx-company/logger'
import { IthacaJwt } from '@dtx-company/ithaca-sdk/src'
import { IthacaRelatedPlanId } from '@dtx-company/app/shared/code/src/hooks/useAddUserToPlanExtra'
import { ParsedUrlQuery } from 'querystring'
import { PaymentMethod, Stripe, StripeElements } from '@stripe/stripe-js'
import { PlanExtraDetail } from '@dtx-company/true-common/src/types/Plan'

export interface BaseSubscriptionArgs {
  promoCode?: string
  token: string
  currentPlanId?: string
  selectedPlanId: string
  planExtraDetails?: PlanExtraDetail[]
  orgName?: string
  subscriptionId?: string
}

export async function addPlanExtraToUser(
  ithacaRelatedPlanId: IthacaRelatedPlanId,
  jwt: IthacaJwt | null
): Promise<void> {
  const token = jwt?.token
  const ithacaId = jwt?.ithacaId
  if (!ithacaRelatedPlanId || !token || !ithacaId) {
    throw new Error('Unable to attach plan')
  } else {
    await fetch(`${AUTHENTICATION_V2_ENDPOINT}/user/`, {
      method: 'PATCH',
      body: JSON.stringify({
        ithacaId,
        roleNames: [`personal_extra:${ithacaRelatedPlanId}`]
      }),
      headers: {
        authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
  }
}

export async function addUserExtras(
  args: {
    query: ParsedUrlQuery
  },
  jwt: IthacaJwt | null
): Promise<void> {
  if (args.query.tc === 'amex') {
    await addPlanExtraToUser(IthacaRelatedPlanId.AMEX_DESIGN, jwt)
  }
}

export const createPaymentMethodFromFormV2 = async ({
  elements,
  stripe
}: {
  elements: StripeElements
  stripe: Stripe
}): Promise<PaymentMethod | undefined> => {
  // Trigger form validation and wallet collection
  await elements.submit()
  return await createPaymentMethodV2({
    elements,
    stripe: stripe
  })
}

export async function createPaymentMethodV2({
  stripe,
  elements
}: {
  elements: StripeElements
  stripe: Stripe
}): Promise<PaymentMethod | undefined> {
  try {
    const result = await stripe.createPaymentMethod({
      elements
    })

    if (result.error) {
      const error = new ClientSideError({
        type: ClientSideErrorType.ClientSideError,
        message: result.error.message || 'createPaymentMethod error',
        name: result.error.code || 'createPaymentMethod'
      })
      throw error
    } else {
      return result.paymentMethod
    }
  } catch (e) {
    const error = new FlowError({
      ...cloneErrorObject(e),
      type: ClientSideErrorType.ClientSideError
    })
    logger.logError(error, { technicalArea: 'monetization' })

    throw error
  }
}
